import { useCallback, useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { fetchNodeExamples } from '../../api';
import { buildNodeParams } from './nodeFilterUtils';
import CallDetailDialog from '../calls/CallDetailDialog';
import useCallDetails from '../calls/useCallDetails';

const moscowDateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit'
});

const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : moscowDateTimeFormatter.format(date);
};

const EXAMPLES_LIMIT = 50;

/** Человеческая подпись значения: коды и ключи пунктов уже есть в каталоге графика. */
const labelOf = (chart, row) => {
  const code = row.itemKey || row.code;
  if (!code) {
    return '—';
  }

  const known = [...(chart.options ?? []), ...(chart.items ?? [])].find((option) => option.code === code);
  return known?.label ?? code;
};

/**
 * Примеры звонков, стоящих за столбцом графика или пунктом чек-листа.
 * Показывает цитату из `call_node_facts.evidence` — то, чем нода обосновала свой вывод.
 *
 * `selection` — { code?, itemKey?, maxScore?, outputKey?, employeeId?, title } — задаёт срез:
 * для свёрнутого графика жалоб (`chart.outputKey === '*'`) категория передаётся как
 * `outputKey`, потому что в фактах она и есть отдельный выход, а не значение.
 */
const NodeExamplesDialog = ({ chart, selection, dateRange, callType, nodeFilter, packVersion, scope, onClose }) => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Карточка звонка открывается поверх списка примеров: закрытие возвращает к тому же
  // срезу, ради которого пользователь сюда кликнул.
  const [detailCallId, setDetailCallId] = useState(null);
  const detail = useCallDetails(detailCallId);

  const load = useCallback(async () => {
    if (!selection) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = {
        ...buildNodeParams({ chart, dateRange, callType, nodeFilter, packVersion, scope }),
        output_key: selection.outputKey ?? chart.outputKey,
        code: selection.code,
        item_key: selection.itemKey,
        max_score: selection.maxScore,
        // Клик по ячейке тепловой карты — это «этот пункт у этого оператора»,
        // без сужения по сотруднику диалог показал бы чужие звонки.
        employee_id: selection.employeeId,
        limit: EXAMPLES_LIMIT
      };

      setRows(await fetchNodeExamples(params));
    } catch {
      setError('Не удалось загрузить примеры звонков.');
    } finally {
      setIsLoading(false);
    }
  }, [chart, selection, dateRange, callType, nodeFilter, packVersion, scope]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Dialog open={Boolean(selection)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent trackId="nodes.examples" className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Примеры звонков</DialogTitle>
          <DialogDescription>
            {chart.title}
            {selection?.title ? ` · ${selection.title}` : ''}
            {` · показываем до ${EXAMPLES_LIMIT} последних`}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
        )}

        {isLoading && <p className="text-sm text-muted-foreground">Загрузка...</p>}

        {!isLoading && !error && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">Примеров за выбранный период нет.</p>
        )}

        {rows.length > 0 && (
          <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {/* На телефоне фиксированные ширины колонок (≈730px) уводили цитату — ради
                      которой диалог и открывают — за правый край. Там остаются время
                      с оператором под ним и цитата, остальное — в деталях звонка. */}
                  <TableHead className="w-10" />
                  <TableHead className="md:w-32">Время</TableHead>
                  <TableHead className="w-40 max-md:hidden">Оператор</TableHead>
                  <TableHead className="w-36 max-md:hidden">Телефон</TableHead>
                  <TableHead className="w-44 max-md:hidden">Значение</TableHead>
                  <TableHead className="w-20 text-right max-md:hidden">Оценка</TableHead>
                  <TableHead>Цитата</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={`${row.callId}-${row.itemKey ?? row.code ?? index}`}>
                    <TableCell>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-ev="nodes.examples.open_call"
                        aria-label="Открыть детали звонка"
                        onClick={() => setDetailCallId(row.callId)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                    <TableCell className="text-xs">
                      {formatDateTime(row.callStartedAt)}
                      <div className="mt-1 text-muted-foreground md:hidden">{row.employeeName || '—'}</div>
                    </TableCell>
                    <TableCell className="text-xs max-md:hidden">{row.employeeName || '—'}</TableCell>
                    <TableCell className="text-xs max-md:hidden">{row.clientPhone || '—'}</TableCell>
                    <TableCell className="text-xs max-md:hidden">{labelOf(chart, row)}</TableCell>
                    <TableCell className="text-right text-xs max-md:hidden">
                      {row.score === null || row.score === undefined ? '—' : row.score.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-xs italic text-slate-600">
                      {row.evidence || row.valueText || (
                        <span className="not-italic text-slate-400">Нода не привела цитату</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <CallDetailDialog
          call={detail.call}
          isLoading={detail.isLoading}
          error={detail.error}
          open={Boolean(detailCallId)}
          onOpenChange={(open) => !open && setDetailCallId(null)}
        />
      </DialogContent>
    </Dialog>
  );
};

export default NodeExamplesDialog;
